/**
 * Namenskonvention an der HTTP-Grenze.
 *
 * Die API benutzt `JsonSerializerDefaults.Web`: Sie **serialisiert** camelCase und
 * **deserialisiert** gross-/kleinschreibungsunabhängig. Der Altclient hat davon nichts gemerkt,
 * weil C# beide Richtungen abgedeckt hat. TypeScript löst Eigenschaften nicht
 * schreibweisenunabhängig auf, deshalb wird hier umgewandelt — an genau einer Stelle, damit der
 * übrige Code (Grid-Spaltennamen, `whereQry`/`orderBy`, Caption- und Feldtyp-Tabellen)
 * durchgängig mit den kanonischen PascalCase-Namen arbeiten kann.
 *
 * Die Vorwärtsrichtung ist eindeutig berechenbar, die Rückrichtung nicht: Aus `uri` folgt nicht
 * `URI`, aus `dl` nicht `DL`, aus `eMail` nicht `EMail`. Sie läuft deshalb ausschliesslich über
 * `canonicalPropertyNames`.
 */

import { allPropertyNames } from '@/types/propertyNames';

/** Pendant zu `char.IsUpper` — Unicode-Kategorie „Uppercase Letter“. */
function isUpperCase(char: string): boolean {
  return /\p{Lu}/u.test(char);
}

/**
 * Portiert `JsonNamingPolicy.CamelCase` (`JsonCamelCaseNamingPolicy.FixCasing`) aus
 * System.Text.Json wortgetreu: `Name` → `name`, `URI` → `uri`, `EMail` → `eMail`,
 * `WKz` → `wKz`, `Container_ETA` → `container_ETA`.
 *
 * Gelesen wird durchgängig aus `name`, geschrieben in `chars`. Das ist zulässig, weil der
 * Algorithmus je Durchlauf nur `chars[i]` schreibt und ausschliesslich `chars[i]` und
 * `chars[i + 1]` liest — beide sind zu diesem Zeitpunkt noch unverändert.
 */
export function toCamelCase(name: string): string {
  if (name.length === 0 || !isUpperCase(name.charAt(0))) {
    return name;
  }

  const chars = name.split('');
  for (let i = 0; i < chars.length; i += 1) {
    const current = name.charAt(i);
    if (i === 1 && !isUpperCase(current)) {
      break;
    }

    const hasNext = i + 1 < chars.length;
    const next = name.charAt(i + 1);
    if (i > 0 && hasNext && !isUpperCase(next)) {
      // Vor einem Leerzeichen wird das aktuelle Zeichen noch klein geschrieben.
      if (next === ' ') {
        chars[i] = current.toLowerCase();
      }
      break;
    }

    chars[i] = current.toLowerCase();
  }
  return chars.join('');
}

/**
 * camelCase-Schlüssel → kanonischer PascalCase-Name des Modells.
 *
 * Der Schlüssel ist die **exakte** camelCase-Form, nicht die kleingeschriebene: `Wkz` und `WKz`
 * existieren beide im Modell (`Receipt.Wkz`, `OrderPosition.WKz`) und ergeben `wkz` bzw. `wKz`.
 */
export const canonicalPropertyNames: ReadonlyMap<string, string> = new Map(
  allPropertyNames.map((name) => [toCamelCase(name), name]),
);

function isArray(value: unknown): value is readonly unknown[] {
  return Array.isArray(value);
}

/** Nur einfache JSON-Objekte werden umgeschlüsselt — `Date`, `Map` & Co. bleiben unangetastet. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype
  );
}

/** Läuft rekursiv über Arrays und einfache Objekte und benennt die Schlüssel um. */
function mapKeys(value: unknown, convert: (key: string) => string): unknown {
  if (isArray(value)) {
    return value.map((item) => mapKeys(item, convert));
  }
  if (!isPlainObject(value)) {
    return value;
  }

  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    result[convert(key)] = mapKeys(item, convert);
  }
  return result;
}

/**
 * Wandelt Antwortdaten rekursiv in kanonische PascalCase-Schlüssel.
 *
 * Unbekannte Schlüssel bleiben unverändert. Das deckt zwei Fälle ab: Felder, die unser Modell
 * nicht kennt (`connectionString`, `erstelltVon` …), und Endpunkte, die entgegen der Annahme
 * schon PascalCase liefern — deren Schlüssel sind bereits kanonisch. Werte werden nie angefasst,
 * insbesondere findet keine Datums- oder Zahlenumwandlung statt.
 */
export function toCanonicalJson<T>(value: unknown): T {
  return mapKeys(value, (key) => canonicalPropertyNames.get(key) ?? key) as T;
}

/** Wandelt Anfragedaten rekursiv in camelCase-Schlüssel — genau das hat der Altclient gesendet. */
export function toWireJson(value: unknown): unknown {
  return mapKeys(value, toCamelCase);
}
